import { useEffect, useState } from "react";
import mammoth from "mammoth";

interface DocxPreviewProps {
  url: string; 
}

export default function DocxPreview({ url }: DocxPreviewProps) {
  const [html, setHtml] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!url) return;

    const fetchDocx = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch DOCX file");

        const arrayBuffer = await response.arrayBuffer();

        const { value } = await mammoth.convertToHtml({ arrayBuffer });

        setHtml(value);
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Error loading document");
      } finally {
        setLoading(false);
      }
    };

    fetchDocx();
  }, [url]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400"></div>
      </div>
    );

  if (error)
    return (
      <div className="text-red-500 text-center p-4 bg-red-100 rounded-md">
        {error}
      </div>
    );

  return (
    <div className="w-full p-6 bg-white rounded-lg shadow-md border border-gray-200">
      {/* Wrapper with CSS for nested elements */}
      <div
        className="docx-preview space-y-4"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <style>{`
        .docx-preview h1 {
          font-size: 2xl;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }
        .docx-preview h2 {
          font-size: xl;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }
        .docx-preview h3 {
          font-size: lg;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }
        .docx-preview p {
          margin-bottom: 0.5rem;
          line-height: 1.6;
          color: #374151;
        }
        .docx-preview ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .docx-preview ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .docx-preview li {
          margin-bottom: 0.25rem;
        }
        .docx-preview a {
          color: #2563eb;
          text-decoration: underline;
        }
        .docx-preview a:hover {
          color: #1d4ed8;
        }
      `}</style>
    </div>
  );
}
